import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../api';
import { useAuth } from '../hooks/useAuth';
import { candidateIdsInClientAtsFromData } from '../utils/clientAtsFilter';
import ModalShell from './ModalShell';
import CandidateProfileModal from './CandidateProfileModal';
import { candidateDisplayName, candidateSearchHaystack } from '../utils/candidateDisplay';
import { jobSkillCounts, normalizeSkillList } from '../utils/skillMatch';
import {
  defaultShowInList,
  MATCH_WEIGHT_SUMMARY,
  overallPercent,
  skillsPercent,
  tierMeta
} from '../utils/matchBreakdown';
import TableExportButton from './TableExportButton';
import MatchAnalyticsModal from './MatchAnalyticsModal';

const MATCH_EXPORT_COLUMNS = [
  { key: 'candidateId', label: 'Candidate ID' },
  { key: 'candidate', label: 'Candidate' },
  { key: 'email', label: 'Email' },
  { key: 'tier', label: 'Fit tier' },
  { key: 'overallPercent', label: 'Overall %' },
  { key: 'skillsPercent', label: 'Skills %' },
  { key: 'matched', label: 'Matched Must-have' },
  { key: 'missing', label: 'Missing Must-have' },
  { key: 'nice', label: 'Matched Nice-to-have' },
  { key: 'experience', label: 'Experience' },
  { key: 'location', label: 'Location' },
  { key: 'fitForSubmission', label: 'Recommended submit' },
  { key: 'factorSummary', label: 'Factor breakdown' }
];

export default function JobMatchesModal({
  job,
  matches = [],
  matchMeta = null,
  onClose,
  onSubmit,
  submitting = false
}) {
  const { user } = useAuth();
  const canOpenProfile = user?.role === 'admin' || user?.role === 'recruiter';
  const [searchQuery, setSearchQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [listMode, setListMode] = useState('recommended');
  const [detailMatch, setDetailMatch] = useState(null);
  const [profileCandidateId, setProfileCandidateId] = useState(null);

  const tierCounts = matchMeta?.tierCounts || {};
  const methodology = matchMeta?.methodology;
  const jobClientId = job?.clientId || matchMeta?.job?.clientId;

  const { data: applications = [] } = useQuery({
    queryKey: ['applications'],
    queryFn: () => api.get('/applications').then(r => r.data),
    enabled: Boolean(jobClientId) && canOpenProfile,
    staleTime: 30_000
  });

  const { data: allJobs = [] } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => api.get('/jobs').then(r => r.data),
    enabled: Boolean(jobClientId) && canOpenProfile,
    staleTime: 60_000
  });

  const atsExcludedIds = useMemo(() => {
    const fromApi = matchMeta?.excludedCandidateIds;
    if (Array.isArray(fromApi) && fromApi.length) {
      return new Set(fromApi.map(id => String(id)));
    }
    return candidateIdsInClientAtsFromData(applications, allJobs, jobClientId);
  }, [matchMeta?.excludedCandidateIds, applications, allJobs, jobClientId]);

  const sourceList = useMemo(() => {
    let list =
      listMode === 'recommended' && matchMeta?.recommended?.length
        ? matchMeta.recommended
        : matches;
    if (atsExcludedIds.size) {
      list = list.filter(row => !atsExcludedIds.has(row.candidate?.id));
    }
    return list;
  }, [listMode, matchMeta, matches, atsExcludedIds]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = [...sourceList];
    if (!showAll) {
      list = list.filter(row => defaultShowInList(row, { showAll: false }));
    }
    if (q) {
      list = list.filter(m => candidateSearchHaystack(m.candidate).includes(q));
    }
    return list.sort((a, b) => (b.rankScore ?? b.score) - (a.rankScore ?? a.score));
  }, [sourceList, searchQuery, showAll]);

  const { must: mustCount, nice: niceCount, total: skillCount } = jobSkillCounts(job);

  const exportRows = filtered.map(matchRow => {
    const candidate = matchRow.candidate || {};
    const factors = (matchRow.matchFactors || [])
      .filter(f => f.applies)
      .map(f => `${f.label}: ${f.percent}% (${f.status})`)
      .join('; ');
    return {
      candidateId: candidate.id || '',
      candidate: candidateDisplayName(candidate),
      email: candidate.displayEmail || candidate.contact?.email || '',
      tier: tierMeta(matchRow.relevanceTier).label,
      overallPercent: `${overallPercent(matchRow)}%`,
      skillsPercent: `${skillsPercent(matchRow)}%`,
      matched: (matchRow.mustMatched || []).join(', '),
      missing: (matchRow.mustMissing || []).join(', '),
      nice: (matchRow.niceMatched || []).join(', '),
      experience:
        candidate.displayExperience ||
        (candidate.experienceYears != null ? `${candidate.experienceYears} yrs` : '—'),
      location: candidate.displayLocation || candidate.location || '—',
      fitForSubmission: matchRow.fitForSubmission ? 'Yes' : 'No',
      factorSummary: factors
    };
  });

  const req = matchMeta?.job?.requirements;

  return (
    <ModalShell
      title="Profile matching — 360° analysis"
      subtitle={
        job
          ? `${job.title}${job.clientName ? ` · ${job.clientName}` : ''}`
          : undefined
      }
      onClose={onClose}
      maxWidth="max-w-6xl"
      footer={
        <>
          <TableExportButton
            filename={`job_matches_${job?.id || 'job'}`}
            sheetName={`Matches ${job?.title || ''}`.trim()}
            columns={MATCH_EXPORT_COLUMNS}
            rows={exportRows}
          />
          <button type="button" className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))]" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <div className="space-y-4 -mx-1">
        <div className="text-xs text-[hsl(var(--ds-fg))] bg-[hsl(var(--ds-muted))] border border-[hsl(var(--ds-border))] p-3 space-y-2">
          <p className="font-medium text-[hsl(var(--ds-fg))]">How matching works</p>
          <p>{methodology?.summary || MATCH_WEIGHT_SUMMARY}</p>
          <p className="text-[hsl(var(--ds-muted-fg))]">
            <strong>Priority:</strong> Skills first, then education and experience, then location, package
            (CTC vs job budget), gender, age band, and working mode. Only criteria set on the job are scored.
          </p>
          {req && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 pt-1 border-t border-[hsl(var(--ds-border))]">
              <p>
                <span className="text-[hsl(var(--ds-muted-fg))]">Core Competencies (must-have):</span>{' '}
                {req.mustHaveSkills?.length ? req.mustHaveSkills.join(', ') : '—'}
              </p>
              <p>
                <span className="text-[hsl(var(--ds-muted-fg))]">Core Competencies (nice-to-have):</span>{' '}
                {req.niceToHaveSkills?.length ? req.niceToHaveSkills.join(', ') : '—'}
              </p>
              <p>
                <span className="text-[hsl(var(--ds-muted-fg))]">Technical Skills (must-have):</span>{' '}
                {req.mustHaveTechnicalSkills?.length ? req.mustHaveTechnicalSkills.join(', ') : '—'}
              </p>
              <p>
                <span className="text-[hsl(var(--ds-muted-fg))]">Technical Skills (nice-to-have):</span>{' '}
                {req.niceToHaveTechnicalSkills?.length ? req.niceToHaveTechnicalSkills.join(', ') : '—'}
              </p>
              <p>
                <span className="text-[hsl(var(--ds-muted-fg))]">Experience:</span>{' '}
                {req.experienceFrom || req.experienceTo
                  ? `${req.experienceFrom || '—'} – ${req.experienceTo || '—'} yrs`
                  : '—'}
              </p>
              <p>
                <span className="text-[hsl(var(--ds-muted-fg))]">Qualification:</span>{' '}
                {(req.educationalQualifications?.length
                  ? req.educationalQualifications
                  : req.educationalQualification
                    ? [req.educationalQualification]
                    : []
                ).join('; ') || '—'}
              </p>
              <p>
                <span className="text-[hsl(var(--ds-muted-fg))]">Location:</span> {req.location || '—'}
              </p>
              <p>
                <span className="text-[hsl(var(--ds-muted-fg))]">Budget:</span>{' '}
                {req.budgetFrom || req.budgetTo
                  ? `${req.budgetFrom || '—'} – ${req.budgetTo || '—'} (INR/yr)`
                  : '—'}
              </p>
            </div>
          )}
          {matchMeta?.excludedFromAtsCount > 0 && (
            <p className="text-[hsl(var(--ds-muted-fg))] pt-1 border-t border-[hsl(var(--ds-border))]">
              <strong>{matchMeta.excludedFromAtsCount}</strong> candidate
              {matchMeta.excludedFromAtsCount === 1 ? '' : 's'} already in ATS for{' '}
              <strong>{job?.clientName || 'this client'}</strong> are hidden from this list.
            </p>
          )}
          {Object.keys(tierCounts).length > 0 && (
            <p className="text-[hsl(var(--ds-muted-fg))] pt-1">
              Pool: {matchMeta?.totalCandidates ?? matches.length} candidates · Strong {tierCounts.strong || 0} ·
              Good {tierCounts.good || 0} · Partial {tierCounts.partial || 0} · Weak {tierCounts.weak || 0} · Not
              relevant {tierCounts.not_relevant || 0}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <input
            type="text"
            className="border border-[hsl(var(--ds-border))] px-3 py-2 text-sm w-full lg:max-w-md"
            placeholder="Search by name, email, ID, skills, location…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="listMode"
                checked={listMode === 'recommended'}
                onChange={() => setListMode('recommended')}
              />
              Top fits (up to 20)
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="listMode"
                checked={listMode === 'all'}
                onChange={() => setListMode('all')}
              />
              All ranked
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
              Include weak / not relevant
            </label>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-8 text-[hsl(var(--ds-muted-fg))] text-sm">
            {skillCount === 0
              ? 'Add must-have or nice-to-have skills on the job to rank candidates.'
              : searchQuery
                ? 'No candidates match your search.'
                : 'No profiles at or above 25% core competency match. Enable “Include weak / not relevant” or add more candidates with matching skills.'}
          </div>
        ) : (
          <div className="border border-[hsl(var(--ds-border))] divide-y divide-[hsl(var(--ds-border))] max-h-[min(58vh,520px)] overflow-y-auto">
            {filtered.map((matchRow, idx) => {
              const { candidate } = matchRow;
              const pct = overallPercent(matchRow);
              const skillPct = skillsPercent(matchRow);
              const tier = tierMeta(matchRow.relevanceTier);
              return (
                <div key={candidate.id} className="bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))]/50">
                  <div className="flex flex-wrap items-start gap-3 p-3">
                    <div className="text-[11px] text-[hsl(var(--ds-muted-fg))] w-6 pt-1">{idx + 1}</div>
                    <div className="flex-1 min-w-[12rem]">
                      <div className="flex flex-wrap items-center gap-2">
                        {canOpenProfile ? (
                          <button
                            type="button"
                            className="font-semibold text-sm text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))] hover:underline underline-offset-2 text-left cursor-pointer"
                            title="View and edit candidate profile"
                            onClick={() => setProfileCandidateId(candidate.id)}
                          >
                            {candidateDisplayName(candidate)}
                          </button>
                        ) : (
                          <span className="font-semibold text-sm text-[hsl(var(--ds-fg))]">
                            {candidateDisplayName(candidate)}
                          </span>
                        )}
                        {canOpenProfile && (
                          <button
                            type="button"
                            className="text-[10px] px-1.5 py-0.5 border border-[hsl(var(--ds-primary)/0.25)] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)]"
                            onClick={() => setProfileCandidateId(candidate.id)}
                          >
                            Profile
                          </button>
                        )}
                        <span
                          className={`text-[10px] px-2 py-0.5 border font-medium ${tier.className}`}
                        >
                          {tier.label}
                        </span>
                        {matchRow.fitForSubmission && (
                          <span className="text-[10px] px-2 py-0.5 bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))] border border-[hsl(var(--ds-primary)/0.25)]">
                            Recommended to submit
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-0.5">
                        {candidate.displayEmail || candidate.contact?.email || '—'} ·{' '}
                        {candidate.displayLocation || candidate.location || '—'}
                        {matchRow.locationDistanceKm != null
                          ? ` · ~${Math.round(matchRow.locationDistanceKm)} km from job`
                          : ''}{' '}
                        ·{' '}
                        {candidate.displayExperience ||
                          (candidate.experienceYears != null
                            ? `${candidate.experienceYears} yrs`
                            : '—')}
                      </p>
                      <div className="flex flex-wrap gap-3 mt-2 text-xs">
                        <span>
                          <strong className="text-[hsl(var(--ds-fg))]">{pct}%</strong> overall
                        </span>
                        {skillCount > 0 && (
                          <span>
                            <strong className="text-green-800">{skillPct}%</strong> skills
                          </span>
                        )}
                        <span className="text-green-700">
                          Must: {(matchRow.mustMatched || []).join(', ') || '—'}
                        </span>
                        {(matchRow.mustMissing || []).length > 0 && (
                          <span className="text-amber-800">
                            Missing: {matchRow.mustMissing.join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        className="px-2 py-1 text-xs border border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                        onClick={() => setDetailMatch(matchRow)}
                      >
                        Details
                      </button>
                      <button
                        type="button"
                        className="px-2 py-1 border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] text-xs hover:bg-[hsl(var(--ds-primary)/0.08)] disabled:opacity-50"
                        disabled={submitting || matchRow.relevanceTier === 'not_relevant'}
                        title={
                          matchRow.relevanceTier === 'not_relevant'
                            ? 'No must-have skill overlap'
                            : 'Add to ATS (internal review before client)'
                        }
                        onClick={() => onSubmit?.(candidate.id)}
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {detailMatch && (
        <MatchAnalyticsModal
          matchRow={detailMatch}
          jobRequirements={req}
          methodology={methodology}
          onClose={() => setDetailMatch(null)}
          onOpenProfile={canOpenProfile ? id => setProfileCandidateId(id) : undefined}
        />
      )}

      {profileCandidateId && (
        <CandidateProfileModal
          candidateId={profileCandidateId}
          onClose={() => setProfileCandidateId(null)}
        />
      )}
    </ModalShell>
  );
}
